import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Download, ExternalLink, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

function StatusRow({ ok, label, detail, warn = false }) {
  const Icon = ok ? CheckCircle2 : warn ? ShieldAlert : XCircle;
  const tone = ok ? "text-emerald-500" : warn ? "text-amber-500" : "text-rose-500";
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border/60 last:border-0">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${tone}`} />
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {detail && <div className="text-xs text-muted-foreground mt-0.5 break-words">{detail}</div>}
      </div>
    </div>
  );
}

function detectIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || "") || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export default function PwaReadinessPanel() {
  const [state, setState] = useState({
    checking: true,
    secure: false,
    manifest: false,
    icon192: false,
    icon512: false,
    swSupported: false,
    swRegistered: false,
    swControlled: false,
    standalone: false,
  });
  const [installPrompt, setInstallPrompt] = useState(() => (typeof window !== "undefined" ? window.__slxPwaInstallPrompt || null : null));

  const check = useCallback(async () => {
    const secure = Boolean(window.isSecureContext || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    const swSupported = "serviceWorker" in navigator;
    let manifest = false;
    let icon192 = false;
    let icon512 = false;
    let swRegistered = false;
    let swControlled = Boolean(navigator.serviceWorker?.controller);

    try {
      const r = await fetch(`/api/public/manifest.webmanifest?diagnostic=${Date.now()}`, { cache: "no-store" });
      if (r.ok) {
        const body = await r.json();
        manifest = Boolean(body?.name && body?.start_url && Array.isArray(body?.icons) && body.icons.length >= 2);
      }
    } catch { /* reported below */ }
    try { icon192 = (await fetch(`/api/public/pwa-icon/192.png?diagnostic=${Date.now()}`, { cache: "no-store" })).ok; } catch { /* no-op */ }
    try { icon512 = (await fetch(`/api/public/pwa-icon/512.png?diagnostic=${Date.now()}`, { cache: "no-store" })).ok; } catch { /* no-op */ }
    if (swSupported && secure) {
      try {
        const reg = await navigator.serviceWorker.getRegistration("/");
        swRegistered = Boolean(reg);
        swControlled = Boolean(navigator.serviceWorker.controller);
      } catch { /* no-op */ }
    }
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    setState({ checking: false, secure, manifest, icon192, icon512, swSupported, swRegistered, swControlled, standalone });
  }, []);

  useEffect(() => {
    check();
    const installable = (e) => setInstallPrompt(e.detail || window.__slxPwaInstallPrompt || null);
    const installed = () => { setInstallPrompt(null); check(); };
    window.addEventListener("slx-pwa-installable", installable);
    window.addEventListener("slx-pwa-installed", installed);
    navigator.serviceWorker?.addEventListener?.("controllerchange", check);
    return () => {
      window.removeEventListener("slx-pwa-installable", installable);
      window.removeEventListener("slx-pwa-installed", installed);
      navigator.serviceWorker?.removeEventListener?.("controllerchange", check);
    };
  }, [check]);

  const ready = useMemo(() => state.secure && state.manifest && state.icon192 && state.icon512 && state.swSupported && state.swRegistered, [state]);
  const isIOS = detectIOS();

  const install = async () => {
    const p = installPrompt || window.__slxPwaInstallPrompt;
    if (!p) return;
    try {
      await p.prompt();
      await p.userChoice;
      window.__slxPwaInstallPrompt = null;
      setInstallPrompt(null);
      check();
    } catch { /* browser owns the UI */ }
  };

  return (
    <div className="border border-border bg-muted/20 p-5 rounded-lg" data-testid="pwa-readiness-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="overline text-primary/60">PWA readiness</div>
          <div className="font-display text-xl mt-1">{ready ? "Ready to install" : "Action required"}</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Live checks from this browser. PWA installation requires HTTPS on real devices; plain HTTP works only on localhost.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={check} disabled={state.checking}>
            <RefreshCw className={`h-4 w-4 mr-2 ${state.checking ? "animate-spin" : ""}`} />Recheck
          </Button>
          {installPrompt && ready && !state.standalone && (
            <Button type="button" size="sm" onClick={install}><Download className="h-4 w-4 mr-2" />Install</Button>
          )}
        </div>
      </div>

      {!state.secure && (
        <div className="mt-4 border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <strong>HTTPS is the blocker on this server.</strong> You are currently using <code>{window.location.origin}</code>. Assign a domain to the VPS and serve SLX-one as <code>https://…</code>; then the service worker can register and the app becomes a real PWA.
        </div>
      )}

      <div className="mt-4 grid md:grid-cols-2 gap-x-6">
        <div>
          <StatusRow ok={state.secure} label="Secure origin (HTTPS)" detail={state.secure ? window.location.origin : `${window.location.origin} is not a secure context`} />
          <StatusRow ok={state.manifest} label="Web app manifest" detail={state.manifest ? "Dynamic manifest is valid and reachable" : "Manifest could not be validated"} />
          <StatusRow ok={state.icon192 && state.icon512} label="Install icons" detail={state.icon192 && state.icon512 ? "192×192 and 512×512 icons are reachable" : "One or more required icon sizes failed"} />
        </div>
        <div>
          <StatusRow ok={state.swSupported} label="Service worker support" detail={state.swSupported ? "Browser supports service workers" : "Unavailable in this browser/origin"} />
          <StatusRow ok={state.swRegistered} warn={state.secure && state.swSupported} label="Service worker registered" detail={state.swRegistered ? "Offline/update worker is registered" : state.secure ? "Reload once after deployment, then recheck" : "Blocked until HTTPS is enabled"} />
          <StatusRow ok={state.standalone} warn={!state.standalone && ready} label="Installed / standalone" detail={state.standalone ? "This window is running as the installed app" : "Currently running in the browser"} />
        </div>
      </div>

      {ready && !state.standalone && !installPrompt && (
        <div className="mt-4 text-xs text-muted-foreground flex items-start gap-2">
          <ExternalLink className="h-4 w-4 flex-shrink-0" />
          <span>{isIOS ? "On iPhone/iPad: Safari → Share → Add to Home Screen. iOS does not expose a programmable Install button." : "If no Install button appears, use the browser menu → Install app / Add to Home screen."}</span>
        </div>
      )}
    </div>
  );
}
